import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import notify, { MESSAGES } from '../../../utils/notify';
import classnames from 'classnames';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import { fetchJson } from '../../Procurement/utils/procurementList';
import { ProductAvatar } from '../../Procurement/component/ListBadges';
import '../../TenderOpening/components/TenderOpening.css';
import '../../Procurement/ProcurementView.css';
import '../TenderEvaluation.css';
import useTenderEvaluationTabVisibility from '../hooks/useTenderEvaluationTabVisibility';
import useTenderEvaluationList, {
  buildTenderEvaluationListUrl,
} from '../hooks/useTenderEvaluationList';
import {
  PROCUREMENT_TYPE_MAP,
  unwrapTenderEvaluationList,
} from '../utils/tenderEvaluationHelpers';
import {
  EVALUATION_STATUSES,
  exportTenderEvaluationList,
  getEvaluationStatus,
} from '../utils/tenderEvaluationList';
import StatusBadge from '../../../Components/Common/StatusBadge';

// `status` is the StatusHps query value ('' = no status filter).
const STAT_CARDS = [
  {
    key: 'total',
    status: '',
    label: 'Total Evaluation',
    icon: 'ri-stack-line',
    tone: 'total',
  },
  ...EVALUATION_STATUSES.map(status => ({
    key: status.value,
    status: status.value,
    label: status.wording,
    icon: status.icon,
    tone: status.stat,
  })),
];

const STATUS_OPTIONS = EVALUATION_STATUSES.map(status => ({
  value: status.value,
  label: status.wording,
}));

// ProcurementType query value -> label (same keys the rows carry).
const PROCUREMENT_TYPE_OPTIONS = Object.entries(PROCUREMENT_TYPE_MAP)
  .filter(([value]) => value)
  .map(([value, label]) => ({ value, label }));

// ContractType query value: 1 = Term, 2 = Spot.
const PERIOD_OPTIONS = [
  { value: '1', label: 'Term' },
  { value: '2', label: 'Spot' },
];

// SortBy value the list hook sends -> column id.
const SORT_FIELD_TO_COLUMN = {
  procurementNumber: 'idProc',
  titleProcurement: 'title',
  procurementType: 'procType',
  statusHps: 'status',
};

// Wordings outside the known list rendered as "primary" (blue) before.
const getStatusTone = wording => getEvaluationStatus(wording)?.tone || 'info';

const StatusPill = ({ wording }) => {
  if (!wording) return <span className='text-muted'>-</span>;

  return (
    <StatusBadge
      tone={getStatusTone(wording)}
      label={wording}
      title={wording}
    />
  );
};

const ProductInfo = ({ item }) => {
  const name = item?.productType?.main;
  const sub = item?.productType?.sub;
  if (!name && !sub) return null;

  return (
    <div className='to-product'>
      <span className='to-product-name'>{name || '-'}</span>
      {sub && <span className='text-truncate ms-2'>· {sub}</span>}
    </div>
  );
};

const StatCard = ({ card, summary, isActive, onSelect }) => {
  const total = summary?.total || 0;
  const count = summary?.[card.key];
  const share =
    card.key !== 'total' && total > 0 && typeof count === 'number'
      ? Math.round((count / total) * 100)
      : null;

  return (
    <button
      type='button'
      className={classnames('to-stat', `to-stat--${card.tone}`, {
        'is-active': isActive,
      })}
      aria-pressed={isActive}
      onClick={onSelect}
    >
      <span className='to-stat-icon'>
        <i className={card.icon} aria-hidden='true' />
      </span>
      <span>
        <span className='to-stat-label'>{card.label}</span>
        <span className='to-stat-value'>
          {typeof count === 'number' ? count : '–'}
        </span>
      </span>
      {share !== null && <span className='to-stat-share'>{share}%</span>}
    </button>
  );
};

const SkeletonRows = ({ count = 5 }) => (
  <div className='w-100 px-3 py-2'>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='d-flex align-items-center gap-3 py-3'>
        <div className='to-skeleton' style={{ width: '9%' }} />
        <div className='to-skeleton' style={{ width: '26%' }} />
        <div className='to-skeleton' style={{ width: '12%' }} />
        <div className='to-skeleton' style={{ width: '18%' }} />
        <div className='to-skeleton flex-grow-1' />
      </div>
    ))}
  </div>
);

const TenderEvalTable = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  exportName = 'List',
}) => {
  const navigate = useNavigate();
  const { canViewProcurementTypeHeader } = useTenderEvaluationTabVisibility();
  const canViewTable = canViewProcurementTypeHeader(procurementTypeHeader);
  const [summary, setSummary] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    debouncedSearchText,
    statusFilter,
    procurementTypeFilter,
    contractTypeFilter,
    sortField,
    sortDirection,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handleProcurementTypeFilter,
    handleContractTypeFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
  } = useTenderEvaluationList({
    procurementTypeHeader,
    refreshTrigger,
    enabled: canViewTable,
  });

  // Counts come from the list endpoint with PageSize=1, reading totalItems.
  useEffect(() => {
    const token = getAuthToken();
    if (!canViewTable || !token || !isAuthenticated()) return undefined;

    const controller = new AbortController();

    Promise.all(
      STAT_CARDS.map(async card => {
        const result = await fetchJson(
          buildTenderEvaluationListUrl({
            currentPage: 1,
            rowsPerPage: 1,
            procurementTypeHeader,
            statusHps: card.status,
          }),
          token,
          controller.signal
        );
        return [card.key, unwrapTenderEvaluationList(result).totalItems];
      })
    )
      .then(entries => setSummary(Object.fromEntries(entries)))
      .catch(error => {
        // The list request already reports load failures; cards just hide counts.
        if (error.name !== 'AbortError') setSummary(null);
      });

    return () => controller.abort();
  }, [canViewTable, procurementTypeHeader, refreshTrigger]);

  // Exports every row matching the current filters, not just this page.
  const handleExport = async format => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsExporting(true);
    try {
      const query = {
        currentPage: 1,
        procurementTypeHeader,
        search: debouncedSearchText,
        statusHps: statusFilter,
        procurementType: procurementTypeFilter,
        contractType: contractTypeFilter,
        sortField,
        sortDirection,
      };
      let result = unwrapTenderEvaluationList(
        await fetchJson(
          buildTenderEvaluationListUrl({
            ...query,
            rowsPerPage: Math.max(totalRows, 1),
          }),
          token
        )
      );
      if (result.totalItems > result.items.length) {
        result = unwrapTenderEvaluationList(
          await fetchJson(
            buildTenderEvaluationListUrl({
              ...query,
              rowsPerPage: result.totalItems,
            }),
            token
          )
        );
      }

      if (!result.items.length) {
        notify.info(MESSAGES.noDataToExport);
        return;
      }
      exportTenderEvaluationList(result.items, {
        format,
        sheetName: exportName,
      });
    } catch (error) {
      notify.apiError(error, 'export tender evaluation data');
    } finally {
      setIsExporting(false);
    }
  };

  if (!canViewTable) {
    return (
      <div className='to-panel'>
        <div className='to-empty'>
          <span className='to-empty-icon'>
            <i className='ri-lock-line' aria-hidden='true' />
          </span>
          <p className='text-muted fs-13 mb-0'>
            You do not have access to view this procurement category.
          </p>
        </div>
      </div>
    );
  }

  const items = data.items || [];
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
    statusFilter && {
      key: 'status',
      label: 'Status',
      value:
        STATUS_OPTIONS.find(o => o.value === statusFilter)?.label ??
        statusFilter,
      onRemove: () => handleStatusFilter(''),
    },
    procurementTypeFilter && {
      key: 'procType',
      label: 'Proc Type',
      value:
        PROCUREMENT_TYPE_OPTIONS.find(o => o.value === procurementTypeFilter)
          ?.label ?? procurementTypeFilter,
      onRemove: () => handleProcurementTypeFilter(''),
    },
    contractTypeFilter && {
      key: 'period',
      label: 'Period',
      value:
        PERIOD_OPTIONS.find(o => o.value === contractTypeFilter)?.label ??
        contractTypeFilter,
      onRemove: () => handleContractTypeFilter(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const goToDetail = row => navigate(`/apps-tenderevaluation/dtl/${row.id}`);

  const emptyState = (
    <EmptyState
      title='No procurement found'
      emptyHint='Procurements appear here for evaluation once suppliers submit their offers.'
      filteredHint='Nothing matches the current search or filters.'
      hasFilters={hasFilters}
      onClear={clearFilters}
    />
  );

  const columns = [
    {
      id: 'idProc',
      name: 'ID Proc',
      selector: row => row.idProc,
      cell: row => <span className='to-id'>{row.idProc || '-'}</span>,
      width: '10%',
      sortable: true,
    },
    {
      id: 'title',
      name: 'Procurement',
      selector: row => row.title,
      cell: row => (
        <div className='d-flex align-items-center gap-2 py-2 w-100'>
          <ProductAvatar name={row.productType?.main} />
          <div className='pv-text-col flex-grow-1'>
            <div className='to-title' title={row.title}>
              {row.title || '-'}
            </div>
            <ProductInfo item={row} />
          </div>
        </div>
      ),
      width: '27%',
      sortable: true,
    },
    {
      id: 'procType',
      name: 'Proc Type',
      selector: row => row.procType,
      cell: row => <span className='to-tag'>{row.procType || '-'}</span>,
      width: '14%',
      sortable: true,
    },
    {
      id: 'productType',
      name: 'Product Type',
      selector: row => row.productType?.main,
      cell: row => (
        <span className='to-product-name'>{row.productType?.main || '-'}</span>
      ),
      width: '12%',
      sortable: true,
    },
    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      cell: row => (
        <span className='pv-desc' title={row.description || ''}>
          {row.description || '-'}
        </span>
      ),
      width: '15%',
      sortable: true,
    },
    {
      id: 'status',
      name: 'Status',
      selector: row => row.evaluationStatusWording,
      cell: row => <StatusPill wording={row.evaluationStatusWording} />,
      width: '14%',
      sortable: true,
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-arrow-right-up-line'
            label='Open detail'
            onClick={e => {
              e.stopPropagation();
              goToDetail(row);
            }}
          />
        </div>
      ),
      width: '8%',
      center: true,
    },
  ];

  const MobileCard = ({ item }) => (
    <div
      className={classnames(
        'to-mcard d-md-none',
        `to-mcard--${getStatusTone(item.evaluationStatusWording)}`
      )}
    >
      <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
        <span className='to-id'>{item.idProc || '-'}</span>
        <StatusPill wording={item.evaluationStatusWording} />
      </div>

      <div className='d-flex align-items-center gap-2'>
        <ProductAvatar name={item.productType?.main} />
        <div className='pv-text-col flex-grow-1'>
          <div className='to-title'>{item.title || '-'}</div>
          <ProductInfo item={item} />
        </div>
      </div>

      <div className='to-mcard-meta'>
        <div>
          <span>Proc Type</span>
          <span>{item.procType || '-'}</span>
        </div>
      </div>

      {item.description && (
        <p className='pv-desc mb-3' title={item.description}>
          {item.description}
        </p>
      )}

      <div className='d-flex gap-2'>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-eye-line'
          className='flex-fill'
          onClick={() => goToDetail(item)}
        >
          View Detail
        </ActionButton>
      </div>
    </div>
  );

  return (
    <>
      <Row className='g-3 mb-3'>
        {STAT_CARDS.map(card => (
          <Col key={card.key} xs={12} sm={6} xl={3}>
            <StatCard
              card={card}
              summary={summary}
              isActive={statusFilter === card.status}
              onSelect={() =>
                handleStatusFilter(
                  statusFilter === card.status && card.status ? '' : card.status
                )
              }
            />
          </Col>
        ))}
      </Row>

      <div className='to-panel'>
        <div className='to-toolbar'>
          <ListSearch
            value={searchText}
            onChange={handleSearch}
            placeholder='Search by title or procurement no...'
            ariaLabel='Search tender evaluation'
          />
          <ListFilterSelect
            label='Status'
            value={statusFilter}
            onChange={handleStatusFilter}
            allLabel='All Status'
            options={STATUS_OPTIONS}
          />
          <ListFilterSelect
            label='Proc Type'
            value={procurementTypeFilter}
            onChange={handleProcurementTypeFilter}
            allLabel='All Proc Type'
            options={PROCUREMENT_TYPE_OPTIONS}
          />
          <ListFilterSelect
            label='Period'
            value={contractTypeFilter}
            onChange={handleContractTypeFilter}
            allLabel='All Period'
            options={PERIOD_OPTIONS}
          />
          <ClearFiltersButton
            count={filterChips.length}
            onClick={clearFilters}
          />
          <span className='to-toolbar-meta'>
            {isLoading
              ? 'Loading...'
              : `Showing ${startEntry}–${endEntry} of ${totalRows} procurements`}
          </span>
          <UncontrolledDropdown>
            <DropdownToggle
              tag='button'
              type='button'
              className='btn btn-light btn-sm d-inline-flex align-items-center'
              disabled={isExporting || isLoading || !totalRows}
            >
              {isExporting ? (
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
              ) : (
                <i className='ri-download-2-line me-2' aria-hidden='true' />
              )}
              Export
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => handleExport('xlsx')}>
                <i className='ri-file-excel-2-line align-middle me-2 text-success'></i>
                Export to Excel
              </DropdownItem>
              <DropdownItem onClick={() => handleExport('csv')}>
                <i className='ri-file-text-line align-middle me-2 text-muted'></i>
                Export to CSV
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <ActiveFilterChips chips={filterChips} />

        <div className='d-md-none p-3'>
          {isLoading && <SkeletonRows count={3} />}
          {!isLoading &&
            items.map((item, index) => (
              <MobileCard key={item.id || index} item={item} />
            ))}
          {!isLoading && !items.length && emptyState}
        </div>

        <div className='d-none d-md-block to-table-wrap'>
          <DataTable
            key={`sort-${sortField}-${sortDirection}`}
            columns={columns}
            data={items}
            pagination={false}
            onSort={handleSort}
            sortServer
            defaultSortFieldId={SORT_FIELD_TO_COLUMN[sortField] || sortField}
            defaultSortAsc={sortDirection === 'asc'}
            onRowClicked={goToDetail}
            highlightOnHover
            pointerOnHover
            persistTableHead
            progressPending={isLoading}
            progressComponent={<SkeletonRows />}
            noDataComponent={emptyState}
            customStyles={{
              headCells: {
                style: { paddingLeft: '12px', paddingRight: '12px' },
              },
              cells: {
                style: {
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  wordBreak: 'break-word',
                },
              },
              table: { style: { width: '100%' } },
              tableWrapper: { style: { display: 'table' } },
            }}
          />
        </div>

        <div className='to-footer'>
          <div className='d-flex align-items-center gap-2'>
            <span>Rows per page</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
              aria-label='Rows per page'
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className='to-footer-range'>
              {startEntry}–{endEntry} of <strong>{totalRows}</strong> items
            </span>
          </div>

          <div className='to-pager'>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(1)}
              aria-label='First page'
              title='First page'
            >
              <i className='ri-skip-back-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(currentPage - 1)}
              aria-label='Previous page'
              title='Previous page'
            >
              <i className='ri-arrow-left-s-line' aria-hidden='true' />
            </button>
            <span className='to-pager-current'>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(currentPage + 1)}
              aria-label='Next page'
              title='Next page'
            >
              <i className='ri-arrow-right-s-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(totalPages)}
              aria-label='Last page'
              title='Last page'
            >
              <i className='ri-skip-forward-line' aria-hidden='true' />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(TenderEvalTable);
